import React, { useMemo } from 'react';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { DEPS_PERCENTIL } from '../../constants/depsPercentil';

ChartJS.register(RadarController, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// Lista de DEPs (label + campos) compartilhada com a Tabela Percentil -
// ver src/constants/depsPercentil.js.
const DEPS = DEPS_PERCENTIL;

// Mesmos cortes e cores do painel de classificacao ao lado ("Diamante -
// Top 1%", "Esmeralda - Top 5%", etc. - ver _avaliacaoGenetica.scss,
// classes .depsText.diamante/.rubi/.esmeralda/.safira/.topazio/.top). Usamos
// os mesmos hex ali definidos para os pontos do radar baterem exatamente com
// o selo que aparece no painel de classificacao.
const CLASSIFICACOES = [
  { nome: 'Diamante', ate: 1, cor: '#A2A3A7' },
  { nome: 'Esmeralda', ate: 5, cor: '#A8DCA8' },
  { nome: 'Rubi', ate: 10, cor: '#F65A70' },
  { nome: 'Safira', ate: 20, cor: '#4B90F0' },
  { nome: 'Topázio Imperial', ate: 30, cor: '#DD895A' },
];
const TOP_MAIORES = { nome: 'TOP Maiores', cor: '#000000' };

function classificar(topPercent) {
  if (topPercent === undefined || topPercent === null || Number.isNaN(parseFloat(topPercent))) return null;
  const valor = parseFloat(topPercent);
  const faixa = CLASSIFICACOES.find((c) => valor <= c.ate);
  return faixa || TOP_MAIORES;
}

// Normaliza um valor bruto de DEP para uma escala 0-100 ("quanto mais perto
// da borda, melhor"), usando a faixa 0.1% (melhor da raca) e 100% (pior da
// raca) da tabela de percentis. Funciona nos dois sentidos (DEP em que maior
// e melhor, ou em que menor e melhor) porque "melhor"/"pior" ja vem certo da
// propria tabela - nao precisamos adivinhar a direcao de cada DEP.
function normalizar(valor, melhor, pior) {
  if (valor === undefined || valor === null || Number.isNaN(parseFloat(valor))) return null;
  if (melhor === undefined || pior === undefined || melhor === pior) return 50;
  const pct = (100 * (parseFloat(valor) - pior)) / (melhor - pior);
  return Math.max(0, Math.min(100, pct));
}

export function RadarDEPs({ dados, percentil, nomeAnimal }) {
  const { labels, animalValues, populacaoValues, tooltips } = useMemo(() => {
    const linhaMelhor = Array.isArray(percentil) && percentil.find((r) => r.top === '0.1%');
    const linhaPior = Array.isArray(percentil) && percentil.find((r) => r.top === '100.0%');
    const linhaMediana = Array.isArray(percentil) && percentil.find((r) => r.top === '50.0%');

    const _labels = [];
    const _animal = [];
    const _populacao = [];
    const _tooltips = [];

    DEPS.forEach((dep) => {
      const rawAnimal = dados ? dados[dep.raw] : undefined;
      const melhor = linhaMelhor ? linhaMelhor[dep.percentil] : undefined;
      const pior = linhaPior ? linhaPior[dep.percentil] : undefined;
      const mediana = linhaMediana ? linhaMediana[dep.percentil] : undefined;

      const valorAnimal = normalizar(rawAnimal, melhor, pior);
      if (valorAnimal === null) return;

      const topAnimal = dados ? dados[dep.top] : undefined;

      _labels.push(dep.label);
      _animal.push(valorAnimal);
      _populacao.push(mediana !== undefined ? normalizar(mediana, melhor, pior) : null);
      _tooltips.push({
        raw: rawAnimal,
        top: topAnimal,
        mediana,
        classificacao: classificar(topAnimal),
      });
    });

    return { labels: _labels, animalValues: _animal, populacaoValues: _populacao, tooltips: _tooltips };
  }, [dados, percentil]);

  if (!dados || labels.length === 0) return null;

  const temPopulacao = populacaoValues.some((v) => v !== null && v !== undefined);

  // Cor de cada ponto = selo de classificacao daquela DEP (Diamante/Esmeralda/
  // Rubi/Safira/Topazio/TOP Maiores), igual ao painel ao lado. A linha e o
  // preenchimento continuam roxos (identidade do animal); so os marcadores
  // mudam de cor - duas informacoes diferentes (quem e o animal x em que
  // faixa ele esta em cada DEP), cada uma na sua propria codificacao.
  const coresPontos = tooltips.map((t) => (t.classificacao ? t.classificacao.cor : '#7A3FC7'));

  const data = {
    labels,
    datasets: [
      {
        label: nomeAnimal || 'Animal',
        data: animalValues,
        backgroundColor: 'rgba(122, 63, 199, 0.25)',
        borderColor: '#7A3FC7',
        borderWidth: 2,
        pointBackgroundColor: coresPontos,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
      ...(temPopulacao
        ? [
            {
              label: 'Mediana da Raça',
              data: populacaoValues,
              backgroundColor: 'rgba(0, 0, 0, 0)',
              borderColor: '#8A8A8A',
              borderWidth: 2,
              borderDash: [6, 4],
              pointBackgroundColor: '#8A8A8A',
              pointRadius: 0,
              pointHoverRadius: 4,
            },
          ]
        : []),
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        min: 0,
        max: 100,
        ticks: {
          stepSize: 25,
          backdropColor: 'transparent',
          color: '#8A8A8A',
          font: { family: 'Roboto', size: 10 },
        },
        grid: { color: '#E4E4E4' },
        angleLines: { color: '#E4E4E4' },
        pointLabels: {
          font: { family: 'Roboto', size: 13, weight: 'bold' },
          color: '#451D73',
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: { family: 'Roboto', size: 13 },
          usePointStyle: true,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const idx = context.dataIndex;
            const info = tooltips[idx] || {};
            if (context.dataset.label === 'Mediana da Raça') {
              return `Mediana da raça: ${info.mediana !== undefined && info.mediana !== null ? info.mediana : '---'}`;
            }
            const top = info.top !== undefined && info.top !== null ? `TOP ${info.top}%` : '---';
            const selo = info.classificacao ? ` — ${info.classificacao.nome}` : '';
            return `${context.dataset.label}: ${info.raw !== undefined && info.raw !== null ? info.raw : '---'} (${top}${selo})`;
          },
        },
      },
    },
  };

  return (
    <div className="radar-deps">
      <Radar data={data} options={options} />
    </div>
  );
}

export default RadarDEPs;
